import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Button, ButtonText, FormError, H3, Icon, Input, Message, Modal, ModalHeader } from 'components';
import { theme, toRem } from 'design';

// For Stacking Elements in a header
interface HeaderContentProps {
  gridGap?: number;
}

export const HeaderContent = styled.div<HeaderContentProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)};
  justify-items: center;
  width: 100%;

  ${({ gridGap }) =>
    gridGap &&
    css`
      grid-gap: ${toRem(gridGap)};
    `}

  > * {
    margin-bottom: 0;
  }
`;

export const StyledFormError = styled(FormError)`
  margin-top: ${toRem(20)};
  margin-bottom: 0;
  align-self: center;
`;

export const StyledErrorSection = styled.section`
  margin-top: ${toRem(20)};
`;

export const Subtitle = styled(H3)`
  font-weight: ${theme.font.weight.regular};
`;

export const InvitationNotice = styled(Message)`
  margin-bottom: ${toRem(33)};
  word-break: break-word;
`;

// Buttons
export const ReviewNowButton = styled(ButtonText)`
  margin-left: ${toRem(5)};
  color: ${theme.color.primary[500]};
`;

export const RemoveButton = styled(ButtonText)`
  color: ${theme.color.danger.medium};
`;

export const UnlinkButton = styled(ButtonText)`
  color: ${theme.color.primary[500]};
  text-decoration: none;
`;

// SelectUser
export const SelectUserModal = styled(Modal)`
  padding-bottom: 0;
`;

export const SelectUserModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(27.5)};
`;

export const SearchInput = styled(Input)`
  align-items: center;
  text-align: center;
`;

export const LoadButton = styled(Button)`
  margin-top: ${toRem(22)};
`;

// Transaction Details
export const PayoutCardRow = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)};
  justify-content: center;
  width: 100%;
  height: auto;
  margin-top: ${toRem(33)};

  @media (min-width: ${theme.breakpoint.xsmall}px) {
    grid-template-columns: repeat(auto-fit, minmax(0, ${toRem(185)}));
  }
`;

const PayoutCardIcon = styled(Icon).attrs({ icon: 'Tooltip' })`
  position: absolute;
  right: ${toRem(12.5)};
  bottom: ${toRem(12.5)};
  display: flex;
  align-self: center;
  width: ${toRem(16.5)};
  height: ${toRem(16.5)};
  margin-left: ${toRem(3.75)};
  fill: ${theme.color.neutral[200]};
  cursor: pointer;
  transition: fill ${theme.transition.icon};
`;

const PayoutCardLayout = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 ${toRem(185)};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${toRem(66)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.neutral[200]};
  color: ${theme.color.neutral[900]};

  label {
    margin-bottom: ${toRem(7)};
  }

  &:hover {
    ${PayoutCardIcon} {
      fill: ${theme.color.neutral[400]};
    }
  }
`;

const PayoutCardLoader = styled(Skeleton)`
  && {
    position: relative;
    display: flex;
    flex: 0 0 ${toRem(185)};
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    height: ${toRem(66)};
    border-radius: ${toRem(5.5)};
  }
`;

interface PayoutCardProps {
  children?: ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const PayoutCard = ({ children, className, isLoading, ...rest }: PayoutCardProps) =>
  isLoading ? (
    <PayoutCardLoader />
  ) : (
    <PayoutCardLayout className={className} {...rest}>
      {children}
    </PayoutCardLayout>
  );

interface TimestampProps {
  date: string;
  time?: string;
}

const TimestampLayout = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  line-height: 100%;
  color: ${theme.color.neutral[900]};
`;

const Date = styled.span`
  position: relative;
  font-size: ${toRem(13.25)};
  font-weight: ${theme.font.weight.medium};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(16.5)};
  }
`;

const Time = styled.span`
  position: relative;
  margin-top: ${toRem(5.5)};
  font-size: ${toRem(13.25)};
  color: ${theme.color.neutral[700]};

  @media (min-width: ${theme.breakpoint.med}px) {
    margin-top: ${toRem(6.5)};
  }
`;

export const PayoutTimestamp = ({ date, time }: TimestampProps) => (
  <TimestampLayout>
    <Date>{date}</Date>
    {time && <Time>{time}</Time>}
  </TimestampLayout>
);
